import {
    ChangeDetectionStrategy,
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    inject,
    input
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NomenclatureType } from '@app/core/models/nomenclature.model';
import { NotificationService } from '@app/core/notifications/notification.service';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { TreeViewService } from '@app/features/editeur/services/tree-view.service';
import { IfIsNotAuthorized, IfNotSuperGestionnaire } from '@app/shared/autorisations/autorisations.decorator.ts';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { Traitement } from '@app/shared/autorisations/traitements';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { PegaseDialogType } from '@pcscol-portail/composants-pegase';
import { Espace, TypeObjetMaquette } from '@pcscol/odf-api-v1-angular';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { FormMatErrorsDirective } from '../../../../../../shared/base-form/form-mat-errors/form-mat-errors.directive';
import { LimitInputValueDirective } from '../../../../../../shared/directives/limit-input-value.directive';
import { AbstractDescripteursComponent } from '../../../../../../shared/form-models/abstract-descripteurs.component';
import { SelectNomenclatureComponent } from '../../../../../../shared/select-nomenclature/select-nomenclature/select-nomenclature.component';
import { SelectStructureComponent } from '../../../../../../shared/select-structure/select-structure.component';
import { DescripteursObjetMaquetteForm } from './descripteurs-objet-maquette-form';

@Component({
    selector: 'app-formulaire-descripteurs-objet-maquette',
    templateUrl: './descripteurs-objet-maquette.component.html',
    styleUrls: ['./descripteurs-objet-maquette.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        SelectNomenclatureComponent,
        MatSlideToggle,
        MatError,
        MatFormField,
        MatLabel,
        MatInput,
        FormMatErrorsDirective,
        SelectStructureComponent,
        LimitInputValueDirective,
        MatButton,
        MatIcon
    ]
})
export class DescripteursObjetMaquetteComponent extends AbstractDescripteursComponent implements OnInit, OnChanges, OnDestroy {
    private fb = inject(NonNullableFormBuilder);
    private commandFactoryService = inject(CommandFactoryService);
    private treeview = inject(TreeViewService);
    private notificationService = inject(NotificationService);
    protected autorisationsService = inject(AutorisationsService);
    private dialogService = inject(DialogService);
    readonly espace = input.required<Espace>();
    readonly objetMaquette = input<ObjetMaquette>();
    readonly typeObjetMaquette = input<TypeObjetMaquette>();

    protected readonly NomenclatureType = NomenclatureType;
    protected readonly TypesObjetMaquette = TypeObjetMaquette;
    @ViewChild('modifierDescripteursObjetMaquetteValideeDialogTmpl') modifierDescripteursObjetMaquetteValideeDialogTmpl: TemplateRef<
        unknown
    >;
    readonly hideSaveButton = input<boolean>(false);
    readonly readonly = input<boolean>(false);
    form: DescripteursObjetMaquetteForm;

    private destroy$ = new Subject<void>();

    ngOnInit() {
        this.form = DescripteursObjetMaquetteForm.createForm(
            this.fb,
            this.typeObjetMaquette()!,
            this.objetMaquette()?.descripteursObjetMaquette
        );
        this.disableFormIfNotAutorised();
        if (this.readonly()) {
            this.disableFormIfNotSuperGestionnaire();
        }
        if (this.objetMaquette()?.contextes?.some(c => c.valide)) {
            this.disablePlageDeChoixToggle();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.typeObjetMaquette && !changes.typeObjetMaquette.isFirstChange()) {
            this.form.updateTypeObjetMaquette(this.typeObjetMaquette()!);
        }
    }

    @IfIsNotAuthorized(Traitement.ODF_MODIFIER_DESCRIPTEURS_OBJET_MAQUETTE)
    disableFormIfNotAutorised() {
        this.form.disable();
    }

    @IfNotSuperGestionnaire()
    disableFormIfNotSuperGestionnaire() {
        this.form.disable();
    }

    disablePlageDeChoixToggle(): void {
        this.form.asFormGroup.controls.plageDeChoix.disable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onTogglePLageDeChoix() {
        if (!this.form.asFormGroup.controls.plageDeChoix.disabled) {
            this.form.togglePLageDeChoix();
        }
    }

    onToggleTeleenseignement() {
        if (!this.form.asFormGroup.disabled) {
            this.form.asFormGroup.controls.teleEnseignement.patchValue(!this.form.asFormGroup.controls.teleEnseignement.value);
            this.form.asFormGroup.controls.teleEnseignement.markAsDirty();
        }
    }

    onToggleStage() {
        if (!this.form.asFormGroup.disabled) {
            this.form.asFormGroup.controls.stage.patchValue(!this.form.asFormGroup.controls.stage.value);
            this.form.asFormGroup.controls.stage.markAsDirty();
        }
    }

    resetForm() {
        this.form.reset();
    }

    copyLibelleCourtToLibelleLong(): void {
        const libelleCourt = this.form.asFormGroup.controls.libelle?.value;
        const libelleLong = this.form.asFormGroup.controls.libelleLong.value;
        if (libelleLong === null && !!libelleCourt) {
            this.form.asFormGroup.controls.libelleLong?.setValue(libelleCourt);
            this.form.asFormGroup.controls.libelleLong?.markAsTouched();
        }
    }

    modifierDescripteursObjetMaquetteHandler() {
        if (this.readonly()) {
            this.modifierDescripteursObjetMaquetteWithConfirmationDialog();
        } else {
            this.modifierDescripteursObjetMaquette();
        }
    }

    private modifierDescripteursObjetMaquetteWithConfirmationDialog() {
        this.dialogService
            .openConfirmationDialog({
                titre: 'Modification en contexte validé',
                template: this.modifierDescripteursObjetMaquetteValideeDialogTmpl,
                type: PegaseDialogType.WARN
            })
            .subscribe((confirmation?: boolean) => {
                if (confirmation) this.modifierDescripteursObjetMaquette();
            });
    }

    private modifierDescripteursObjetMaquette(): void {
        if (this.objetMaquette && this.form.isValid) {
            this.commandFactoryService
                .modifierDescripteurObjetMaquetteCommand(
                    this.objetMaquette()!.id!,
                    this.objetMaquette()!.version,
                    this.objetMaquette()!.descripteursObjetMaquette!,
                    this.form.asDescripteursObjetMaquette
                )
                .do()
                .pipe(first(), takeUntil(this.destroy$))
                .subscribe(() => {
                    // this.objetMaquette = om; // TODO risque de désynchro, à tester
                    this.treeview.refresh();
                    this.notificationService.success(`Les descripteurs de l'objet ont été enregistrés avec succès`);
                });
        }
    }
}
